import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../api";
import Glyph from "./navGlyphs";
import ReplyPanel from "./ReplyPanel";
import { BOARD_STAGE_LABELS } from "./ui";

/*
 * MEETINGS ▸ CALENDAR — his Google calendar, with every call tied to its company.
 *
 * Built against Gabriel's mockup (Calendar.dc.html). The events are live from
 * Google (src/calendarView.js); a call with a CRM company shows the company and
 * its stage, and opens beside the grid; anything else — the standup, lunch — is
 * drawn plainly and opens nothing.
 *
 * A PAST CALL SAYS WHETHER IT HAPPENED, from the same reading the no-show
 * rescue uses: a transcript with them on it, an empty room, or unknown. On an
 * unknown call the panel asks him, and his answer outranks the evidence
 * everywhere. "They didn't show" also opens the rescue in the composer as a
 * draft for him to review and send himself — never sent by itself (ruled
 * 2026-09-30).
 *
 * Times are drawn in his zone (Meetings ▸ Availability's, Israel), with his
 * bookable window shaded and the late US band hatched, so he can see where a
 * lead could land.
 *
 * Departures from the mockup: "Open prep notes" is not offered on an upcoming
 * call, because there are no prep notes to open — the call's join link is, when
 * it has one. The panel's words describe what actually happened (a rescue queued
 * or sent, or none) rather than the mockup's sample sentences.
 */

const ZONE = "Asia/Jerusalem";
const PX = 72; // pixels per hour in the week grid
const DAY_MS = 86400000;
const DN = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

// Wall-clock parts of an instant in his zone.
function parts(value) {
  const d = new Date(value);
  const p = Object.fromEntries(
    new Intl.DateTimeFormat("en-GB", {
      timeZone: ZONE,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      weekday: "short",
      hourCycle: "h23",
    })
      .formatToParts(d)
      .map((x) => [x.type, x.value])
  );
  return {
    key: `${p.year}-${p.month}-${p.day}`,
    minutes: Number(p.hour) * 60 + Number(p.minute),
    clock: `${p.hour}:${p.minute}`,
    weekday: DN.indexOf(p.weekday),
  };
}

// Local midnight of a day key, as a Date (used only for stepping and labels).
function dayFromKey(key) {
  const [y, m, d] = key.split("-").map(Number);
  return new Date(Date.UTC(y, m - 1, d, 12));
}
function keyOf(date) {
  return date.toISOString().slice(0, 10);
}
function addDays(key, n) {
  return keyOf(new Date(dayFromKey(key).getTime() + n * DAY_MS));
}
function label(key, opts) {
  return dayFromKey(key).toLocaleDateString("en-GB", { timeZone: "UTC", ...opts });
}
function toMin(hhmm) {
  if (!hhmm) return null;
  const [h, m] = hhmm.split(":").map(Number);
  return h * 60 + m;
}

// Sunday that starts the week holding `key` (his week starts on Sunday).
function weekStart(key) {
  return addDays(key, -dayFromKey(key).getUTCDay());
}

// Side-by-side lanes for overlapping events in one day column.
function lanes(events) {
  const sorted = [...events].sort((a, b) => a.s - b.s || b.e - a.e);
  const out = [];
  let group = [];
  let groupEnd = -1;
  const flush = () => {
    const cols = [];
    for (const ev of group) {
      let col = cols.findIndex((end) => end <= ev.s);
      if (col === -1) col = cols.push(0) - 1;
      cols[col] = ev.e;
      ev.col = col;
    }
    for (const ev of group) out.push({ ...ev, cols: cols.length });
    group = [];
  };
  for (const ev of sorted) {
    if (ev.s >= groupEnd && group.length) flush();
    group.push(ev);
    groupEnd = Math.max(groupEnd, ev.e);
  }
  if (group.length) flush();
  return out;
}

function StatusIcon({ status, size = 12 }) {
  if (status === "happened") return <span className="crm-cal__st crm-cal__st--ok" title="The call happened">✓</span>;
  if (status === "empty") return <span className="crm-cal__st crm-cal__st--empty" title="Empty room" style={{ width: size + 3, height: size + 3 }}>×</span>;
  if (status === "unknown") return <span className="crm-cal__st crm-cal__st--unknown" title="Unknown">?</span>;
  return null;
}

function stageLabel(stage) {
  return BOARD_STAGE_LABELS[stage] || stage || "";
}

/* ------------------------------------------------------------- panel */

function statusCopy(ev) {
  if (ev.status === "upcoming") {
    const hours = Math.round((new Date(ev.start) - Date.now()) / 3600000);
    return {
      title: "Upcoming",
      body: hours < 1 ? "Starting within the hour." : hours < 24 ? `In ${hours} hours.` : `In ${Math.round(hours / 24)} days.`,
    };
  }
  if (ev.status === "happened") {
    return ev.marked === "held"
      ? { title: "Happened", body: "You marked this call as held." }
      : { title: "Transcript arrived", body: "The call happened: Fireflies' transcript has somebody from their side on it." };
  }
  if (ev.status === "empty") {
    const rescue = ev.rescue
      ? ev.rescue.outcome === "QUEUED"
        ? " The no-show rescue has an email queued."
        : ` The no-show rescue emailed them${ev.rescue.sent_at ? ` on ${new Date(ev.rescue.sent_at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}` : ""}.`
      : " No rescue has gone out.";
    return ev.marked === "no_show"
      ? { title: "No-show", body: `You marked this as a no-show.${rescue}` }
      : { title: "Empty room", body: `Nobody from their side joined: the transcript has only us on it.${rescue}` };
  }
  return {
    title: "Unknown",
    body: "No transcript arrived, so the CRM can't tell whether they came. Say which, and the no-show rescue goes by your answer.",
  };
}

function Panel({ ev, onClose, onMark, onDraft, busy }) {
  const co = ev.company;
  const s = parts(ev.start);
  const e = parts(ev.end);
  const copy = statusCopy(ev);
  const when = `${label(s.key, { weekday: "short", day: "numeric", month: "short" })} · ${s.clock}–${e.clock}`;

  let primary = null;
  if (ev.status === "upcoming" && ev.meet_link) {
    primary = (
      <a className="crm-cal__primary" href={ev.meet_link} target="_blank" rel="noreferrer">
        Join call
      </a>
    );
  } else if (ev.status === "happened") {
    primary = (
      <button type="button" className="crm-cal__primary" onClick={() => onDraft("call_followup")}>
        Draft follow-up
      </button>
    );
  } else if (ev.status === "empty") {
    primary = (
      <button type="button" className="crm-cal__primary" onClick={() => onDraft("no_show_rescue")}>
        Offer new times
      </button>
    );
  }

  return (
    <aside className="crm-cal__panel">
      <div className="crm-cal__phead">
        <div>
          <span className="crm-cal__muted">{co.domain}</span>
          <h2>{co.name}</h2>
        </div>
        <button type="button" className="crm-cal__close" onClick={onClose} aria-label="Close">
          ×
        </button>
      </div>
      <div className="crm-cal__pmeta">
        {co.stage && <span className="crm-cal__tag">{stageLabel(co.stage)}</span>}
        {co.score != null && (
          <span>
            <b className="crm-num">{co.score}</b> <span className="crm-cal__muted">{co.band}</span>
          </span>
        )}
        {co.country && <span className="crm-cal__muted">· {co.country}</span>}
      </div>

      <div className="crm-cal__psec">
        <span className="crm-cal__eyebrow">This call</span>
        <div>
          <b>{when}</b>
          <span className="crm-cal__muted">
            {ev.meet_link ? "Google Meet · " : ""}with {co.contact.name}
          </span>
        </div>
        {ev.booked && (
          <span className="crm-cal__booked">
            <Glyph name="calendar" size={14} /> Booked through your booking page
          </span>
        )}
        <div className="crm-cal__status">
          {ev.status !== "upcoming" && <StatusIcon status={ev.status} size={14} />}
          <div>
            <b>{copy.title}</b>
            <span>{copy.body}</span>
          </div>
        </div>
        {ev.status === "unknown" && (
          <div className="crm-cal__ask">
            <button type="button" disabled={busy} onClick={() => onMark("held")}>
              It happened
            </button>
            <button type="button" disabled={busy} onClick={() => onMark("no_show")}>
              They didn't show
            </button>
          </div>
        )}
        {ev.marked && (
          <button type="button" className="crm-cal__undo" disabled={busy} onClick={() => onMark(null)}>
            Take back your answer
          </button>
        )}
      </div>

      <div className="crm-cal__psec">
        <span className="crm-cal__eyebrow">Contact</span>
        <div className="crm-cal__contact">
          <span className="crm-cal__avatar">
            {(co.contact.name || "?")
              .split(" ")
              .map((x) => x[0])
              .join("")
              .slice(0, 2)}
          </span>
          <div>
            <b>{co.contact.name}</b>
            <span className="crm-cal__muted">{co.contact.title || co.contact.email}</span>
          </div>
        </div>
      </div>

      {co.recent.length > 0 && (
        <div className="crm-cal__psec crm-cal__psec--grow">
          <span className="crm-cal__eyebrow">Recent</span>
          {co.recent.map((r, i) => (
            <div key={i} className="crm-cal__recent">
              <span className="crm-num">{new Date(r.at).toLocaleDateString("en-GB", { day: "numeric", month: "short" })}</span>
              <span>{r.title || r.type}</span>
            </div>
          ))}
        </div>
      )}

      <div className="crm-cal__pfoot">
        {primary}
        <Link to={`/contacts/${co.contact.id}`} className="crm-cal__open">
          Open record →
        </Link>
      </div>
    </aside>
  );
}

/* ------------------------------------------------------------- page */

export default function CalendarPage() {
  const todayKey = parts(new Date()).key;
  const [view, setView] = useState("week");
  const [anchor, setAnchor] = useState(todayKey);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(null);
  const [now, setNow] = useState(() => parts(new Date()));

  useEffect(() => {
    const t = setInterval(() => setNow(parts(new Date())), 60000);
    return () => clearInterval(t);
  }, []);

  // The days on screen: seven from Sunday, or whole weeks covering the month.
  const days = useMemo(() => {
    if (view === "week") {
      const first = weekStart(anchor);
      return Array.from({ length: 7 }, (_, i) => addDays(first, i));
    }
    const [y, m] = anchor.split("-").map(Number);
    const monthFirst = `${y}-${String(m).padStart(2, "0")}-01`;
    const first = weekStart(monthFirst);
    const out = [];
    for (let i = 0; i < 42; i++) {
      const key = addDays(first, i);
      if (i >= 28 && i % 7 === 0 && Number(key.slice(5, 7)) !== m) break;
      out.push(key);
    }
    return out;
  }, [view, anchor]);

  const load = useCallback(async () => {
    setError(null);
    // Padded a day either side so a zone offset never drops an edge event.
    const from = new Date(dayFromKey(days[0]).getTime() - DAY_MS).toISOString();
    const to = new Date(dayFromKey(days[days.length - 1]).getTime() + 2 * DAY_MS).toISOString();
    try {
      setData(await api(`/calendar?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`));
    } catch (err) {
      setError(err.message);
    }
  }, [days]);

  useEffect(() => {
    load();
  }, [load]);

  // Keep the open panel in step with a reload.
  const selectedEvent = selected && data ? data.events.find((e) => e.id === selected) || null : null;

  const byDay = useMemo(() => {
    const map = new Map(days.map((d) => [d, []]));
    for (const ev of (data && data.events) || []) {
      if (ev.all_day) {
        const k = ev.start.slice(0, 10);
        if (map.has(k)) map.get(k).push(ev);
        continue;
      }
      const s = parts(ev.start);
      if (map.has(s.key)) map.get(s.key).push(ev);
    }
    return map;
  }, [data, days]);

  const mark = async (outcome) => {
    const ev = selectedEvent;
    if (!ev) return;
    setBusy(true);
    try {
      await api("/calendar/outcome", {
        method: "POST",
        body: JSON.stringify({ event_id: ev.id, outcome, company_id: ev.company.id }),
      });
      await load();
      if (outcome === "no_show") setDrafting({ kind: "no_show_rescue", ev });
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const availability = data && data.availability;
  const bookable = availability ? availability.bookable_window : [];
  const usLate = availability ? toMin(availability.us_late_until) : null;

  // Hours the week grid spans: the bookable window and the US band, widened to
  // take in any timed event outside them.
  const [h0, h1] = useMemo(() => {
    let lo = 9 * 60;
    let hi = 19 * 60;
    for (const w of bookable) if (w) {
      lo = Math.min(lo, toMin(w[0]));
      hi = Math.max(hi, toMin(w[1]));
    }
    if (usLate) hi = Math.max(hi, usLate);
    if (view === "week") {
      for (const d of days) for (const ev of byDay.get(d) || []) {
        if (ev.all_day) continue;
        lo = Math.min(lo, parts(ev.start).minutes);
        const e = parts(ev.end);
        hi = Math.max(hi, e.key === parts(ev.start).key ? e.minutes : 24 * 60);
      }
    }
    return [Math.max(0, Math.floor(lo / 60) - 1), Math.min(24, Math.ceil(hi / 60) + 1)];
  }, [bookable, usLate, days, byDay, view]);

  const step = (dir) => {
    if (view === "week") setAnchor(addDays(anchor, 7 * dir));
    else {
      const [y, m] = anchor.split("-").map(Number);
      const d = new Date(Date.UTC(y, m - 1 + dir, 1, 12));
      setAnchor(keyOf(d));
    }
  };

  const rangeLabel =
    view === "week"
      ? `${label(days[0], { day: "numeric", month: "short" })} – ${label(days[6], { day: "numeric", month: "short", year: "numeric" })}`
      : label(anchor, { month: "long", year: "numeric" });

  const Event = ({ ev, compact }) => {
    const s = parts(ev.start);
    if (!ev.company) {
      return (
        <div className={`crm-cal__ev crm-cal__ev--plain${compact ? " is-compact" : ""}`}>
          {!ev.all_day && <span className="crm-cal__time crm-num">{s.clock}</span>}
          <span className="crm-cal__evtitle">{ev.title}</span>
        </div>
      );
    }
    return (
      <button
        type="button"
        className={`crm-cal__ev crm-cal__ev--co${compact ? " is-compact" : ""}${selected === ev.id ? " is-selected" : ""}`}
        onClick={() => setSelected(ev.id)}
      >
        <div className="crm-cal__evline">
          <span className="crm-cal__time crm-num">{s.clock}</span>
          <span className="crm-cal__evtitle">{ev.company.name}</span>
          {compact && ev.booked && <Glyph name="calendar" size={11} />}
          {compact && <StatusIcon status={ev.status} size={10} />}
        </div>
        {!compact && (
          <div className="crm-cal__evline">
            {ev.company.stage && <span className="crm-cal__tag crm-cal__tag--sm">{stageLabel(ev.company.stage)}</span>}
            {ev.booked && (
              <span title="Booked via booking page">
                <Glyph name="calendar" size={12} />
              </span>
            )}
            <span className="crm-cal__grow" />
            <StatusIcon status={ev.status} />
          </div>
        )}
      </button>
    );
  };

  return (
    <div className="crm-cal">
      <main className="crm-cal__main">
        <header className="crm-cal__head">
          <div className="crm-cal__toprow">
            <div className="crm-cal__title">
              <div className="crm-cal__crumb">
                <span>Meetings</span>
                <Glyph name="chevronRight" size={12} />
                <span>Calendar</span>
              </div>
              <h1>Calendar</h1>
            </div>
            <button type="button" className="crm-cal__today" onClick={() => setAnchor(todayKey)}>
              <b>Today</b>
              <span className="crm-cal__muted">
                {label(todayKey, { weekday: "short", day: "numeric", month: "short" })} · {now.clock}
              </span>
            </button>
            <div className="crm-cal__nav">
              <button type="button" onClick={() => step(-1)} aria-label="Earlier">
                <Glyph name="chevronRight" size={16} className="crm-cal__flip" />
              </button>
              <button type="button" onClick={() => step(1)} aria-label="Later">
                <Glyph name="chevronRight" size={16} />
              </button>
              <span className="crm-cal__range">{rangeLabel}</span>
            </div>
            <div className="crm-cal__seg">
              <button type="button" className={view === "week" ? "is-on" : ""} onClick={() => setView("week")}>
                Week
              </button>
              <button type="button" className={view === "month" ? "is-on" : ""} onClick={() => setView("month")}>
                Month
              </button>
            </div>
            <Link to="/meetings/availability" className="crm-cal__gear" title="Availability" aria-label="Availability">
              <Glyph name="settings" size={17} />
            </Link>
          </div>
          <div className="crm-cal__legend">
            <span><i className="crm-cal__sw" />Bookable</span>
            <span><i className="crm-cal__sw crm-cal__sw--us" />US leads only</span>
            <span><Glyph name="calendar" size={13} />Booked</span>
            <span><StatusIcon status="happened" />Happened</span>
            <span><StatusIcon status="empty" />Empty room</span>
            <span><StatusIcon status="unknown" />Unknown</span>
            <span className="crm-cal__grow" />
            <span className="crm-cal__muted">From Google Calendar</span>
          </div>
        </header>

        <div className="crm-cal__scroll">
          {error && <p className="crm-cal__bad">{error}</p>}
          {data && data.connected === false && (
            <div className="crm-cal__warn">
              <b>Google Calendar is not connected.</b> Reconnect it to see your calls here.{" "}
              <a href={`/api/gmail/auth?redirect=1&token=${encodeURIComponent(localStorage.getItem("dashboard_token") || "")}`}>
                Reconnect Google
              </a>
            </div>
          )}

          {view === "week" ? (
            <div className="crm-cal__grid">
              <div className="crm-cal__days" style={{ gridTemplateColumns: `52px repeat(7, minmax(0, 1fr))` }}>
                <div />
                {days.map((d) => (
                  <div key={d} className="crm-cal__dayhead">
                    <span className="crm-cal__muted">{label(d, { weekday: "short" })}</span>
                    <span className={d === todayKey ? "crm-cal__daynum is-today" : "crm-cal__daynum"}>{Number(d.slice(8))}</span>
                  </div>
                ))}
              </div>
              {days.some((d) => (byDay.get(d) || []).some((e) => e.all_day)) && (
                <div className="crm-cal__allday" style={{ gridTemplateColumns: `52px repeat(7, minmax(0, 1fr))` }}>
                  <span className="crm-cal__muted">all day</span>
                  {days.map((d) => (
                    <div key={d}>
                      {(byDay.get(d) || []).filter((e) => e.all_day).map((ev) => (
                        <Event key={ev.id} ev={ev} compact />
                      ))}
                    </div>
                  ))}
                </div>
              )}
              <div className="crm-cal__body" style={{ gridTemplateColumns: `52px repeat(7, minmax(0, 1fr))` }}>
                <div className="crm-cal__hours" style={{ height: (h1 - h0) * PX }}>
                  {Array.from({ length: h1 - h0 - 1 }, (_, i) => (
                    <span key={i} style={{ top: (i + 1) * PX }} className="crm-num">
                      {String(h0 + i + 1).padStart(2, "0")}:00
                    </span>
                  ))}
                </div>
                {days.map((d) => {
                  const wd = dayFromKey(d).getUTCDay();
                  const w = bookable[wd];
                  const top = (m) => ((m - h0 * 60) / 60) * PX;
                  const timed = (byDay.get(d) || [])
                    .filter((e) => !e.all_day)
                    .map((ev) => {
                      const s = parts(ev.start);
                      const e = parts(ev.end);
                      const end = e.key === s.key ? e.minutes : 24 * 60;
                      return { ev, s: s.minutes, e: Math.max(end, s.minutes + 20) };
                    });
                  return (
                    <div key={d} className="crm-cal__col" style={{ height: (h1 - h0) * PX, backgroundSize: `100% ${PX}px` }}>
                      {w && <div className="crm-cal__win" style={{ top: top(toMin(w[0])), height: top(toMin(w[1])) - top(toMin(w[0])) }} />}
                      {w && usLate && usLate > toMin(w[1]) && (
                        <div className="crm-cal__us" style={{ top: top(toMin(w[1])), height: top(usLate) - top(toMin(w[1])) }} />
                      )}
                      {lanes(timed).map(({ ev, s, e, col, cols }) => (
                        <div
                          key={ev.id}
                          className="crm-cal__slot"
                          style={{
                            top: top(s) + 1,
                            height: ((e - s) / 60) * PX - 2,
                            left: `calc(${(col / cols) * 100}% + 3px)`,
                            width: `calc(${100 / cols}% - 6px)`,
                          }}
                        >
                          {/* A half-hour call is too short for two lines: one line,
                              with the booked and status marks kept on it. */}
                          <Event ev={ev} compact={((e - s) / 60) * PX < 44} />
                        </div>
                      ))}
                      {d === todayKey && now.minutes >= h0 * 60 && now.minutes <= h1 * 60 && (
                        <>
                          <div className="crm-cal__now" style={{ top: top(now.minutes) }} />
                          <div className="crm-cal__nowdot" style={{ top: top(now.minutes) - 3 }} />
                        </>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ) : (
            <div className="crm-cal__grid">
              <div className="crm-cal__days" style={{ gridTemplateColumns: "repeat(7, minmax(0, 1fr))" }}>
                {DN.map((n) => (
                  <div key={n} className="crm-cal__dayhead">
                    <span className="crm-cal__muted">{n}</span>
                  </div>
                ))}
              </div>
              {Array.from({ length: days.length / 7 }, (_, w) => (
                <div key={w} className="crm-cal__week">
                  {days.slice(w * 7, w * 7 + 7).map((d) => {
                    const evs = byDay.get(d) || [];
                    const inMonth = d.slice(5, 7) === anchor.slice(5, 7);
                    return (
                      <div key={d} className={`crm-cal__cell${bookable[dayFromKey(d).getUTCDay()] ? "" : " is-off"}`}>
                        <span className={d === todayKey ? "crm-cal__daynum is-today" : `crm-cal__daynum${inMonth ? "" : " is-out"}`}>
                          {d.slice(8) === "01" ? label(d, { day: "numeric", month: "short" }) : Number(d.slice(8))}
                        </span>
                        {evs.slice(0, 3).map((ev) => (
                          <Event key={ev.id} ev={ev} compact />
                        ))}
                        {evs.length > 3 && <span className="crm-cal__more">+{evs.length - 3} more</span>}
                      </div>
                    );
                  })}
                </div>
              ))}
            </div>
          )}
        </div>
      </main>

      {selectedEvent && selectedEvent.company && (
        <Panel
          ev={selectedEvent}
          busy={busy}
          onClose={() => setSelected(null)}
          onMark={mark}
          onDraft={(kind) => setDrafting({ kind, ev: selectedEvent })}
        />
      )}

      {drafting && (
        <div className="crm-fu__panel">
          <ReplyPanel
            action={{
              kind: drafting.kind,
              verb: drafting.kind === "no_show_rescue" ? "Offer new times" : "Follow up",
              contact_id: drafting.ev.company.contact.id,
              contact_email: drafting.ev.company.contact.email,
              event_id: drafting.kind === "no_show_rescue" ? drafting.ev.id : null,
            }}
            onClose={() => {
              setDrafting(null);
              load();
            }}
          />
        </div>
      )}
    </div>
  );
}
